<script setup>
  import {computed, ref} from "vue";

  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentRiskDonutChart from "@components/Risks/RiskDonutChart.vue";
  import ComponentTableRowCheckbox from "@components/Table/TableRowCheckbox.vue";
  import {medicationMeal} from "@enums/medicationEnums.js";
  import {usePage} from "@inertiajs/vue3";
  import ComponentRecordProgress from "@pages/Records/Components/RecordProgress.vue";
  import ComponentRecordStatus from "@pages/Records/Components/RecordStatus.vue";
  import {recordNotifications, recordOriginTexts, sharedrecordStatus} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedHoverCard, MedIcon, MedLink, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "PatientsDocumentRecordRow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    selectedRows: {
      type: Array,
      required: true,
    },
  });

  defineEmits([
    "confirm:deleteItem",
    "open:shareRecord",
    "open:shareStatus",
    "open:editRecordLabel",
    "update:selectedRows",
  ]);

  const page = usePage();

  const expanded = ref(false);

  const hasResult = computed(() => props.record.progress !== "formEmpty" && props.record.progress !== "formFilled");

  const sharedByMe = computed(() => props.record.sharedata.shared_by_me);

  const isRevoked = computed(() => props.record.sharedata.shared_status === sharedrecordStatus.revoked);
  const isShareable = computed(
    () => props.record.sharedata.can_share_this_record ?? (!isRevoked.value && hasResult.value),
  );

  const isDeletable = computed(() => {
    return props.record.is_pos_record ? page.props.can.delete_wawi_records : page.props.can.delete_records;
  });

  const deleteTooltipText = computed(() => {
    if (isDeletable.value) {
      return "Löschen";
    }

    if (props.record.is_pos_record) {
      return "Sie können die Analyse nicht löschen, da es sich um eine Analyse aus der Warenwirtschaft handelt";
    }

    return "Sie können die Analyse nicht löschen, da Berechtigungen fehlen";
  });

  const recordEditLink = computed(() => {
    return route("records.edit", {patient: props.patient.id, record: props.record.id});
  });

  const recordOrigin = computed(() => {
    if (props.record.origin) {
      return recordOriginTexts[props.record.origin] ?? null;
    }

    return null;
  });

  // /** Object contains value in (optional) key */
  // function contains(objects, value, key = "record_id") {
  //   return objects && objects.some((object) => object[key] === value);
  // }

  function getNotificationIcon(notification) {
    if (notification?.type === recordNotifications.sharedByMe) {
      return "call_made";
    }

    if (notification?.type === recordNotifications.sharedWithMe) {
      return "call_received";
    }
    if (notification?.type === recordNotifications.takenAwayFromMe) {
      return "person_add_disabled";
    }
    if (notification?.type === recordNotifications.revokedByMe) {
      return "person_add_disabled";
    }
    if (notification?.type === recordNotifications.completedFromMe) {
      return "done";
    }

    if (notification?.type === recordNotifications.returnedToMe) {
      return "keyboard_backspace";
    }

    if (notification?.type === recordNotifications.editedFromMe) {
      return "edit";
    }
    if (notification?.type === recordNotifications.commented) {
      return "mode_comment";
    }
  }

  function getNotificationPartner(notification) {
    if (notification?.audience === "group") {
      return "Seminar-/Link-Freigabe";
    }

    if (notification?.type === recordNotifications.commented && !notification?.partner_name) {
      // von ...
      return "extern hinzugezogenem Arzt";
    }

    return notification?.partner_company || notification?.partner_name;
  }

  function getNotificationText(notification) {
    let text = notification.updated_at ?? "zuletzt";

    if (notification?.type === recordNotifications.sharedByMe) {
      return text + " von mir freigegeben" + (notification?.audience === "group" ? " als" : " an");
    }

    if (notification?.type === recordNotifications.sharedWithMe) {
      return text + " für mich freigegeben von";
    }
    if (notification?.type === recordNotifications.takenAwayFromMe) {
      return text + " widerrufen von";
    }
    if (notification?.type === recordNotifications.revokedByMe) {
      return text + " von mir widerrufen gegenüber";
    }
    if (notification?.type === recordNotifications.completedFromMe) {
      return text + " Zurückgesendet an";
    }

    if (notification?.type === recordNotifications.returnedToMe) {
      return text + (notification?.with_changes ? " mit Änderungen" : "") + " an mich zurückgesendet von";
    }

    if (notification?.type === recordNotifications.editedFromMe) {
      return text + " von mir bearbeitet";
    }
    if (notification?.type === recordNotifications.commented) {
      return text + " kommentiert von";
    }
  }

  function toggleRow(event) {
    let node = event.target ?? event;

    if (!node.nodeName) {
      return;
    }

    if (
      node.nodeName.toLowerCase() === "a" ||
      node.nodeName.toLowerCase() === "button" ||
      node.nodeName.toLowerCase() === "select" ||
      node.nodeName.toLowerCase() === "input" ||
      node.nodeName.toLowerCase() === "textarea" ||
      node.nodeName.toLowerCase() === "dialog"
    ) {
      // click on a clickable element (a or button)
      return;
    }

    if (node.nodeName.toLowerCase() === "td") {
      // click on <td> => TOGGLE row
      expanded.value = !expanded.value;
      return;
    }

    if (node.parentNode) {
      // click on any other element: check if parent is <td>
      return toggleRow(node.parentNode);
    }
  }
</script>

<template>
  <tr
    class="cursor-row-resize leading-tight hover:bg-gray-100"
    :class="{
      'border-b-2 border-gray-200': !expanded,
      'bg-gray-200': selectedRows.includes(record.id),
    }"
    :data-test="getTestId('row')"
    @click="(event) => toggleRow(event)"
  >
    <td class="px-0.5 py-2">
      <!-- type | label & 🍩 risk donut -->
      <div class="flex flex-1 justify-between">
        <div>
          <!-- ☑️ multi-select (or 🔻 toggle)  & type -->
          <div class="flex items-center space-x-2">
            <ComponentTableRowCheckbox
              v-if="$page.props?.can?.allow_multi_select"
              :row-key="record.id"
              :selected-rows="selectedRows"
              :test-id="getTestId('row')"
              @update:selected-rows="(newSelectedRows) => $emit('update:selectedRows', newSelectedRows)"
            />

            <div v-else>
              <MedTooltip :content="expanded ? 'einklappen' : 'ausklappen'" side="left">
                <button
                  class="flex cursor-pointer items-center rounded-full leading-none"
                  :aria-label="expanded ? 'Einklappen' : 'Ausklappen'"
                  :aria-expanded="expanded"
                  @click="expanded = !expanded"
                >
                  <MedIcon
                    size="lg"
                    icon="keyboard_arrow_down"
                    :class="
                      cn('transition-transform duration-200', {
                        'rotate-180': expanded,
                        'text-mcred': !expanded,
                      })
                    "
                  />
                </button>
              </MedTooltip>
            </div>

            <MedLink
              as="inertia"
              class="font-semibold"
              :href="recordEditLink"
              :data-test="getTestId('row-edit-record-inertia-link')"
            >
              <span class="text-sm md:hidden">Analyse</span>
              <span class="hidden md:inline">Medikations&shy;analyse</span>
            </MedLink>
          </div>

          <!-- (🔻 toggle &) label -->
          <div class="flex items-center space-x-2">
            <div v-if="$page.props?.can?.allow_multi_select">
              <MedTooltip :content="expanded ? 'einklappen' : 'ausklappen'" side="left">
                <button
                  class="flex cursor-pointer items-center rounded-full leading-none"
                  :aria-label="expanded ? 'Einklappen' : 'Ausklappen'"
                  :aria-expanded="expanded"
                  @click="expanded = !expanded"
                >
                  <MedIcon
                    size="lg"
                    icon="keyboard_arrow_down"
                    :class="
                      cn('transition-transform duration-200', {
                        'rotate-180': expanded,
                        'text-mcred': !expanded,
                      })
                    "
                  />
                </button>
              </MedTooltip>
            </div>

            <div class="h-6" :class="{'ml-6': !$page.props?.can?.allow_multi_select}">
              <MedTooltip v-if="record.label" content="Interne Bezeichnung">
                <MedLink
                  as="button"
                  size="xs"
                  class="text-left"
                  :data-test="getTestId('row-edit-record-label-inertia-link')"
                  @click.prevent="$emit('open:editRecordLabel')"
                >
                  {{ record.label }}
                </MedLink>
              </MedTooltip>

              <template v-else>
                <MedLink
                  as="button"
                  variant="primary"
                  size="xs"
                  class="text-left"
                  :data-test="getTestId('row-edit-record-label-inertia-link')"
                  @click.prevent="$emit('open:editRecordLabel')"
                >
                  Bezeichnung vergeben
                </MedLink>
              </template>
            </div>
          </div>
        </div>

        <ComponentRiskDonutChart :medications="record.medications" :embedded-in-row="true" />
      </div>
    </td>

    <!-- progress -->
    <td class="px-0.5 py-2">
      <ComponentRecordProgress :record="record" :transfer="record.transfer" />
    </td>

    <!-- created at -->
    <td class="px-0.5 py-2">
      {{ record.created_at }}

      <div v-if="recordOrigin">
        <MedTooltip content="Herkunft">
          <span class="text-xs">
            {{ recordOrigin }}
          </span>
        </MedTooltip>
      </div>
    </td>

    <!-- updated at -->
    <td class="px-0.5 py-2">
      {{ record.updated_at }}

      <div v-if="record.username">
        <MedTooltip content="Bearbeiter">
          <span class="text-xs">{{ record.username }}</span>
        </MedTooltip>
      </div>
    </td>

    <!-- sharing & events -->
    <td class="px-0.5 py-2">
      <div class="flex space-x-2">
        <!-- share button -->
        <template
          v-if="
            $page.props.can.view_shared_records &&
            !isRevoked &&
            (!patient.is_fake || $page.props.can.allow_shared_document_forwarding) &&
            $page.props.client.license.is_pro
          "
        >
          <MedTooltip v-if="isShareable && sharedByMe" content="Variante erneut teilen (wurde bereits geteilt)">
            <MedButton
              aria-label="Analyse erneut teilen"
              variant="primary"
              icon="share"
              size="xs"
              rounded="full"
              class="border"
              :data-test="getTestId('row-open-share-record-dialog-button')"
              @click="$emit('open:shareRecord')"
            />
          </MedTooltip>

          <MedTooltip v-else-if="isShareable" content="Variante teilen (wurde bislang noch nicht geteilt)">
            <MedButton
              :aria-label="record.label ? `${record.label} teilen` : 'Analyse teilen'"
              flat
              variant="primary"
              icon="share"
              size="xs"
              rounded="full"
              class="border border-mcred"
              :data-test="getTestId('row-open-share-record-dialog-button')"
              @click="$emit('open:shareRecord')"
            />
          </MedTooltip>

          <MedTooltip v-else content="Ohne Analyseergebnis kann diese Variante nicht geteilt werden">
            <MedButton
              aria-label="Analyse teilen"
              disabled
              icon="share"
              size="xs"
              rounded="full"
              class="border"
              :data-test="getTestId('row-disabled-share-record-dialog-button')"
            />
          </MedTooltip>
        </template>

        <!-- share status button (always available due to list of comments) -->
        <template v-if="record?.notifications.length">
          <MedHoverCard>
            <!-- "question_answer" or specific icon via getNotificationIcon(record.notifications[0]) -->
            <MedButton
              aria-label="Freigabestatus und Verlauf anzeigen"
              flat
              icon="question_answer"
              size="xs"
              rounded="full"
              class="border"
              :data-test="getTestId('row-open-share-record-dialog-button')"
              @click="$emit('open:shareStatus')"
            />

            <template #content>
              <div class="flex items-center space-x-2">
                <MedIcon :icon="getNotificationIcon(record.notifications[0])" />

                <div>
                  <div class="font-thin">{{ getNotificationText(record.notifications[0]) }}</div>
                  <div>{{ getNotificationPartner(record.notifications[0]) }}</div>
                </div>
              </div>
            </template>
          </MedHoverCard>
        </template>
      </div>
    </td>

    <td class="px-0.5 py-2">
      <div class="pr-1 md:pr-2">
        <ComponentRecordStatus :record="record" inner-class="min-w-24 max-w-32" />
      </div>
    </td>

    <!-- actions -->
    <td class="px-0.5 py-2">
      <div class="flex justify-center space-x-1">
        <MedTooltip :content="deleteTooltipText">
          <MedButton
            :aria-label="record.label ? `${record.label} löschen` : 'Analyse löschen'"
            flat
            icon="delete"
            rounded="full"
            :disabled="!isDeletable"
            :data-test="getTestId('row-delete-button')"
            @click="$emit('confirm:deleteItem')"
          />
        </MedTooltip>
      </div>
    </td>
  </tr>

  <!-- expandable details -->
  <tr
    v-if="expanded"
    class="border-b-2 border-gray-200 leading-tight [:hover+&]:bg-gray-100"
    :class="{'bg-gray-200': selectedRows.includes(record.id)}"
  >
    <td colspan="7" class="pl-6 text-sm">
      <table v-if="record.medications.length > 0" class="w-full text-xs md:text-sm">
        <thead>
          <tr class="text-gray-700">
            <th class="w-4/12 px-1 py-0.5 text-left align-top font-semibold md:w-3/12 lg:w-2/12">Wirkstoff</th>
            <th class="hidden px-1 py-0.5 text-left align-top font-semibold md:table-cell md:w-2/12 lg:w-1/12">
              Stärke
            </th>
            <th class="w-3/12 px-1 py-0.5 text-left align-top font-semibold md:w-3/12 lg:w-3/12">Handelsname</th>
            <th class="w-2/12 px-1 py-0.5 text-left align-top font-semibold md:w-2/12 lg:w-2/12">Dosierung</th>
            <th class="hidden px-1 py-0.5 text-left align-top font-semibold lg:table-cell lg:w-1/12">Einheit</th>
            <th class="w-3/12 px-1 py-0.5 text-left align-top font-semibold md:w-2/12 lg:w-3/12">Grund</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="medication in record.medications" :key="medication.id" class="border-t-1 border-gray-200">
            <td class="px-1 py-0.5">
              {{ medication.substances.map((substance) => substance.name).join("\n") }}
            </td>

            <td class="hidden px-1 py-0.5 md:table-cell">
              {{ medication.substances.map((substance) => substance.strength).join("\n") }}
            </td>

            <td class="px-1 py-0.5">{{ medication.fam }}</td>

            <td class="px-1 py-0.5">
              <div v-if="medication.dosage.dosagealternative === null" class="whitespace-nowrap">
                {{ medication.dosage.dosage_string_formatted }}
              </div>
              <div v-else>
                {{ medication.dosage.dosagealternative.text }}
              </div>

              <span v-if="medication?.dosage?.meal" class="flex text-xs text-gray-500">
                {{ medicationMeal[medication?.dosage?.meal] }}
              </span>
            </td>

            <td class="hidden px-1 py-0.5 lg:table-cell">
              <div>{{ medication.dosage.selected_unit }}</div>
            </td>

            <td class="px-1 py-0.5">
              {{ medication.indications.map((indication) => indication.name).join(", ") }}
            </td>
          </tr>
        </tbody>
      </table>

      <ComponentEmptyState v-else class="mt-0 mb-2" :test-id="getTestId()">
        Diese Variante enthält noch keine Medikation.
      </ComponentEmptyState>
    </td>
  </tr>
</template>
